import { fmtPln } from './client-offer-review-format';

export interface ClientOfferReviewBannerModel {
  /** Tailwind DS classes for the banner frame (border + background). */
  frameClassName: string;
  title: string;
  titleClassName: string;
  desc: string | null;
  descClassName: string;
  icon: 'ok' | 'x' | 'clock';
}

/** Translated banner/pill copy — the real route composes these via `t()`. */
export interface ClientOfferReviewBannerModelLabels {
  acceptedTitle: string;
  acceptedDescription: (accCount: number, accValueLabel: string) => string;
  rejectedTitle: string;
  rejectedDescription: string;
  expiredTitle: string;
  expiredDescription: string;
  previewTitle: string;
  previewDescription: string;
  pendingTitle: string;
  statusAccepted: string;
  statusRejected: string;
  statusExpired: string;
  statusSent: string;
}

/**
 * Frame/title/desc classes keyed by the same success|destructive|warning semantic
 * variants as the DS Badge component (packages/ui/src/components/badge.tsx), so the
 * five banner states below derive from ONE definition per variant instead of each
 * hand-rolling its own emerald/red/amber trio. The banner itself stays a full-width
 * bordered strip (not a pill) so it can't literally render <Badge>, but the color
 * grouping mirrors Badge's variant vocabulary for consistency.
 */
type BannerVariant = 'success' | 'destructive' | 'warning';

const BANNER_VARIANT_CLASSES: Record<
  BannerVariant,
  { frameClassName: string; titleClassName: string; descClassName: string }
> = {
  success: {
    frameClassName: 'border-emerald-200 bg-emerald-50',
    titleClassName: 'text-emerald-800',
    descClassName: 'text-emerald-700',
  },
  destructive: {
    frameClassName: 'border-red-200 bg-red-50',
    titleClassName: 'text-red-800',
    descClassName: 'text-red-700',
  },
  warning: {
    frameClassName: 'border-amber-200 bg-amber-50',
    titleClassName: 'text-amber-800',
    descClassName: 'text-amber-700',
  },
};

function acceptedBanner(
  accCount: number,
  accValue: number,
  labels: ClientOfferReviewBannerModelLabels,
): ClientOfferReviewBannerModel {
  return {
    ...BANNER_VARIANT_CLASSES.success,
    title: labels.acceptedTitle,
    desc: labels.acceptedDescription(accCount, fmtPln(accValue) ?? ''),
    icon: 'ok',
  };
}

function rejectedBanner(labels: ClientOfferReviewBannerModelLabels): ClientOfferReviewBannerModel {
  return {
    ...BANNER_VARIANT_CLASSES.destructive,
    title: labels.rejectedTitle,
    desc: labels.rejectedDescription,
    icon: 'x',
  };
}

function expiredBanner(labels: ClientOfferReviewBannerModelLabels): ClientOfferReviewBannerModel {
  return {
    ...BANNER_VARIANT_CLASSES.destructive,
    title: labels.expiredTitle,
    desc: labels.expiredDescription,
    icon: 'x',
  };
}

function previewBanner(labels: ClientOfferReviewBannerModelLabels): ClientOfferReviewBannerModel {
  return {
    ...BANNER_VARIANT_CLASSES.warning,
    title: labels.previewTitle,
    desc: labels.previewDescription,
    icon: 'clock',
  };
}

function pendingBanner(labels: ClientOfferReviewBannerModelLabels): ClientOfferReviewBannerModel {
  return {
    ...BANNER_VARIANT_CLASSES.warning,
    title: labels.pendingTitle,
    desc: null,
    icon: 'clock',
  };
}

/** Top status banner state derived from offer state / local accept-reject flags. */
export function computeOfferReviewBanner(args: {
  isAccepted: boolean;
  isRejected: boolean;
  isExpired: boolean;
  isPreview: boolean;
  accCount: number;
  accValue: number;
  labels: ClientOfferReviewBannerModelLabels;
}): ClientOfferReviewBannerModel {
  const { isAccepted, isRejected, isExpired, isPreview, accCount, accValue, labels } = args;
  if (isAccepted) return acceptedBanner(accCount, accValue, labels);
  if (isRejected) return rejectedBanner(labels);
  if (isExpired) return expiredBanner(labels);
  if (isPreview) return previewBanner(labels);
  return pendingBanner(labels);
}

export interface ClientOfferReviewStatusPillModel {
  label: string;
  /** DS Badge (`@ooh/ui/components/badge`) variant to render the pill with. */
  variant: 'success' | 'destructive' | 'info';
}

/** Header status pill, mirrors the banner state mapping. */
export function computeOfferReviewStatusPill(args: {
  isAccepted: boolean;
  isRejected: boolean;
  isExpired: boolean;
  labels: ClientOfferReviewBannerModelLabels;
}): ClientOfferReviewStatusPillModel {
  const { labels } = args;
  if (args.isAccepted) return { label: labels.statusAccepted, variant: 'success' };
  if (args.isRejected) return { label: labels.statusRejected, variant: 'destructive' };
  if (args.isExpired) return { label: labels.statusExpired, variant: 'destructive' };
  return { label: labels.statusSent, variant: 'info' };
}
